import { use, useEffect, useState, type ComponentType } from 'react';
import { Spinner } from '../components/ScanZone/ScanZone';

/** A stage chunk that didn't load (offline, or a deploy replaced it): the shell says so. */
export class ChunkLoadError extends Error {
  constructor(cause: unknown) {
    super('A part of the app could not be loaded', { cause });
    this.name = 'ChunkLoadError';
  }
}

/**
 * A screen from a lazily loaded stage chunk. Unlike React.lazy, a failed load isn't kept: the
 * error goes to the shell's boundary once, and the next mount (its "Try again") loads again.
 */
export type LazyScreen = ComponentType & { preload: () => Promise<unknown> };

export function lazyScreen<M>(load: () => Promise<M>, pick: (m: M) => ComponentType): LazyScreen {
  let loading: Promise<ComponentType> | null = null;
  let Loaded: ComponentType | null = null;
  const get = () =>
    (loading ??= load().then(
      (m) => (Loaded = pick(m)),
      (e: unknown) => {
        loading = null;
        throw new ChunkLoadError(e);
      },
    ));
  /* Suspends until the stage is in. Its own component: once loaded, LazyScreen renders the
     screen directly and this one is dropped, so no render calls use() conditionally. */
  function Waiting() {
    const C = use(get());
    // One component per screen, loaded once and kept: not a new component type per render.
    // eslint-disable-next-line react-hooks/static-components
    return <C />;
  }
  function LazyScreen() {
    return Loaded ? <Loaded /> : <Waiting />;
  }
  /** Starts (or joins) the load; never rejects (a failure is the next mount's business). */
  LazyScreen.preload = () => get().catch(() => undefined);
  return LazyScreen;
}

/** While a stage loads: nothing for 400 ms (usually enough), then a spinner. */
export function StageLoading() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 400);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="stage-loading" aria-busy="true">
      {slow && <Spinner />}
    </div>
  );
}
